import React, { useEffect, useRef } from 'react';
import { useTranslate } from '../hooks/useTranslate';
import { AI_DISCLAIMER_TEXT } from '../constants';

interface DisclaimerModalProps {
    isOpen: boolean;
    onClose: () => void;
}

// Modern AI Disclaimer Modal with enhanced styling
export const DisclaimerModal: React.FC<DisclaimerModalProps> = ({ isOpen, onClose }) => {
    const { t } = useTranslate();
    const dialogRef = useRef<HTMLDivElement | null>(null);
    const closeBtnRef = useRef<HTMLButtonElement | null>(null);

    // Focus management
    useEffect(() => {
        if (isOpen) {
            // Slight delay to ensure element is mounted
            setTimeout(() => closeBtnRef.current?.focus(), 0);
        }
    }, [isOpen]);

    // Escape key close
    useEffect(() => {
        if (!isOpen) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose();
            }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-[10000] flex items-center justify-center px-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="disclaimer-title"
            aria-describedby="disclaimer-body"
        >
            <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-gray-900/50 to-black/70 backdrop-blur-md" onClick={() => { }} />
            <div
                ref={dialogRef}
                className="relative w-full max-w-2xl bg-gradient-to-br from-white via-gray-50 to-white rounded-3xl border border-gray-200/50 shadow-2xl overflow-hidden transform transition-all duration-300"
                style={{
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #ffffff 100%)',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)'
                }}
            >
                {/* Header with AI Icon */}
                <div className="bg-gradient-to-r from-red-600 via-red-700 to-red-800 px-8 py-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            {/* AI Brain Icon */}
                            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                                <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v-.07zM17.9 17.39c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
                                    <circle cx="15.5" cy="9.5" r="1.5" fill="currentColor" />
                                    <circle cx="8.5" cy="12.5" r="1" fill="currentColor" />
                                </svg>
                            </div>
                            <div>
                                <h2 id="disclaimer-title" className="text-2xl font-bold text-white tracking-tight">
                                    {t('AI Disclaimer')}
                                </h2>
                                <p className="text-red-100 text-sm font-medium">
                                    {t('Artificial Intelligence Notice')}
                                </p>
                            </div>
                        </div>
                        <button
                            ref={closeBtnRef}
                            onClick={onClose}
                            className="w-10 h-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center text-white transition-all duration-200 hover:scale-105"
                            aria-label="Close disclaimer"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-8">
                    <div
                        id="disclaimer-body"
                        className="space-y-6 max-h-[50vh] overflow-y-auto pr-2"
                        style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e0 transparent' }}
                    >
                        {/* Main Description */}
                        <div className="bg-gradient-to-r from-red-50 to-red-100 rounded-2xl p-6 border border-red-200">
                            <p className="text-gray-800 text-base leading-relaxed">
                                {t('This application uses AI to analyze your data, identify potential data quality issues, and suggest remediation strategies.')}
                            </p>
                        </div>

                        {/* Important Notice */}
                        <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-6 border border-amber-200">
                            <div className="flex items-start space-x-3">
                                <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <svg className="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-amber-900 mb-3">{t('Important:')}</h3>
                                    <ul className="space-y-2 text-gray-700">
                                        <li className="flex items-start space-x-2">
                                            <span className="w-2 h-2 bg-amber-400 rounded-full flex-shrink-0 mt-2"></span>
                                            <span>{t('AI outputs may be incomplete, outdated, or contextually imperfect.')}</span>
                                        </li>
                                        <li className="flex items-start space-x-2">
                                            <span className="w-2 h-2 bg-amber-400 rounded-full flex-shrink-0 mt-2"></span>
                                            <span>{t('Always validate critical findings before acting.')}</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="bg-gray-50/80 backdrop-blur-sm px-8 py-4 border-t border-gray-200/50">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2 text-sm text-gray-500">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                            <span>{t('Use AI insights responsibly')}</span>
                        </div>
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-medium rounded-xl transition-all duration-200 hover:scale-105 shadow-lg"
                        >
                            {t('I Understand')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DisclaimerModal;